import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cs from "classnames/bind";
import { publicConfig } from "@lululemon/ecom-core";
import { StaticMap } from "@lululemon/dsp-legacy-shared";
import { useWindowDimensions } from "@lululemon/dsp-legacy-shared/src/utils";
import styles from "./contact-us.module.scss";
import ContactUsContent from "./contact-us-content";
import {
  STORE_DETAILS_EXPERIENTIAL,
  STORE_DETAILS_REGULAR
} from "../../content/analytics-tags";

const linkAttributesExperiential = {
  "data-component-link": STORE_DETAILS_EXPERIENTIAL
};
const linkAttributesRegular = {
  "data-component-link": STORE_DETAILS_REGULAR
};

const ContactUs = ({
  fullAddress,
  email,
  phone,
  isExperiential,
  latitude,
  longitude
}) => {
  // HYDRATION MISMATCH CAUSE
  let isMobileView = false;
  if (process.browser) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    isMobileView = useWindowDimensions().width < 992;
  }
  const directionsLink = `${publicConfig.googleMapsSearchDomain}${fullAddress}`;

  const linkAttributesByStoreType = isExperiential
    ? linkAttributesExperiential
    : linkAttributesRegular;

  // These props are identical for both small and large maps.
  const staticMapSharedProps = {
    latitude,
    longitude,
    href: directionsLink,
    linkAttributes: linkAttributesByStoreType
  };

  /**
   * The following refs and hooks are used to calculate dimensions for the StaticMap component,
   * based on the size of its surrounding elements. As the screen is resized, map height/width
   * props will change, causing the map URL to be regenerated. These hooks allow the map to
   * properly shrink/expand responsively.
   */
  const mapSmallRef = useRef(null);
  const [mapSmallDimensions, setmapSmallDimensions] = useState({
    height: mapSmallRef?.current?.clientHeight || 200,
    width: mapSmallRef?.current?.clientWidth || 0
  });

  useEffect(() => {
    /**
     * When showing large map for 'regular' store desktop view, use combined height of all
     * elements in first column. Reduce height for mobile view (regular and experiential).
     */
    const updatemapSmall = () => {
      setmapSmallDimensions({
        height: 200,
        width: mapSmallRef.current?.clientWidth
      });
    };

    const updateMapDimensions = () => {
      updatemapSmall();
    };

    // Immediately call updateMapDimensions so values are set on initial page load.
    updateMapDimensions();

    window.addEventListener("resize", updateMapDimensions);

    return () => window.removeEventListener("resize", updateMapDimensions);
  }, []);

  return (
    <div className={cs("lll-grid", styles.contactusContainer)}>
      <div className={styles.leftPanel}>
        <h2 className={styles.heading}>Contact Us</h2>
      </div>
      <div className={cs("OneLinkNoTx", styles.rightPanel)}>
        <ContactUsContent
          fullAddress={fullAddress}
          email={email}
          phone={phone}
          isExperiential={isExperiential}
        />
      </div>
      {isExperiential && !isMobileView && (
        <div ref={mapSmallRef} className={styles.mapContainerSmall}>
          <StaticMap
            {...staticMapSharedProps}
            height={mapSmallDimensions.height}
            width={mapSmallDimensions.width}
          />
        </div>
      )}
    </div>
  );
};

ContactUs.propTypes = {
  fullAddress: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  phone: PropTypes.string.isRequired,
  isExperiential: PropTypes.bool.isRequired,
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired
};

export default ContactUs;
